// 구독자 직접 입력(CSV 텍스트)의 형식 검증.
//
// 서버는 파일 업로드와 완전히 같은 경로(Subscription::UpsertAllByCsv)로 처리하므로,
// 여기서는 "그 파서가 받아들이는 형태인지" 를 입력 즉시 알려주는 역할만 한다.
// 서버 규칙과 맞춰야 하는 지점:
//   - CSV.parse(headers: true) — 첫 줄은 헤더로 소비된다. 헤더가 없으면 첫 구독자가 조용히 사라진다.
//   - 열 순서: email, nickname, marketing_agreement
//   - marketing_agreement 는 문자열 "true" 일 때만 참으로 본다
//   - 같은 이메일이 여러 번 나오면 뒤엣것은 무시된다

export const EXPECTED_HEADER = "email,nickname,marketing_agreement";

// 서버(URI::MailTo::EMAIL_REGEXP)와 동등한 수준의 실용 검사
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const normalizeHeaderCell = (cell) => cell.trim().toLowerCase().replace(/^["']|["']$/g, "");

// 따옴표로 감싼 값 안의 콤마를 존중하는 최소 CSV 분해
export function splitCsvLine(line) {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      cells.push(current);
      current = "";
    } else {
      current += char;
    }
  }

  cells.push(current);
  return cells.map((cell) => cell.trim());
}

export function looksLikeHeader(line) {
  const cells = splitCsvLine(line).map(normalizeHeaderCell);
  return cells.includes("email");
}

/**
 * @param {string} text textarea 원문
 * @returns {{errors: Array, warnings: Array, validCount: number, duplicateCount: number}}
 */
export function validateSubscriberCsv(text) {
  const result = { errors: [], warnings: [], validCount: 0, duplicateCount: 0 };
  const raw = (text || "").replace(/\r\n?/g, "\n");

  if (raw.trim() === "") return result;

  const lines = raw.split("\n");
  const firstContentIndex = lines.findIndex((line) => line.trim() !== "");

  if (firstContentIndex === -1) return result;

  if (!looksLikeHeader(lines[firstContentIndex])) {
    result.errors.push({
      line: firstContentIndex + 1,
      message: `첫 줄은 헤더여야 해요. 맨 위에 "${EXPECTED_HEADER}" 를 넣어주세요. (헤더가 없으면 첫 번째 구독자가 등록되지 않아요)`,
    });
    return result;
  }

  const seen = new Set();

  lines.forEach((line, index) => {
    if (index <= firstContentIndex) return;
    if (line.trim() === "") return;

    const lineNumber = index + 1;
    const cells = splitCsvLine(line);

    if (cells.length > 3) {
      result.errors.push({ line: lineNumber, message: "열이 3개(email, nickname, marketing_agreement)보다 많아요." });
      return;
    }

    const email = (cells[0] || "").replace(/\s+/g, "");

    if (email === "") {
      result.errors.push({ line: lineNumber, message: "이메일이 비어 있어요." });
      return;
    }

    if (!EMAIL_PATTERN.test(email)) {
      result.errors.push({ line: lineNumber, message: `이메일 형식이 올바르지 않아요: ${email}` });
      return;
    }

    const marketingAgreement = (cells[2] || "").trim().toLowerCase();
    if (marketingAgreement !== "" && marketingAgreement !== "true" && marketingAgreement !== "false") {
      result.errors.push({
        line: lineNumber,
        message: `마케팅 수신 동의는 true 또는 false 로 적어주세요: ${cells[2]}`,
      });
      return;
    }

    const key = email.toLowerCase();
    if (seen.has(key)) {
      result.duplicateCount += 1;
      result.warnings.push({ line: lineNumber, message: `앞에 이미 나온 이메일이라 한 번만 등록돼요: ${email}` });
      return;
    }

    seen.add(key);
    result.validCount += 1;
  });

  return result;
}
